import { AxiosResponse } from 'axios';
import $api from '@/http/index';
import { IHistorySearchItem } from '@models/search/IHistorySearchItem';
import { ISearchDictResults } from '@models/search/ISearchDictResultItem';
import { ISearchDocResults } from '@models/search/ISearchDocResultItem';
import { ISearchExtraFlowSetting } from '@models/search/ISearchExtraFilters';
import { ISearchReportResults } from '@models/search/ISearchReportResultItem';
import { ISearchRequest } from '@models/search/search.types';
import { IForms } from '@models/Forms/IForms';
import { ITabsVisibility } from '@models/search/ITabsVisibility';
import { IResultTilesSetting } from '@models/search/IResultTilesSetting';
import { IActionResultData } from '@models/actions/IActionResult';

/**
 * Единая нормализация пагинации/сортировки для всех областей: раньше документы и
 * справочники считали `skip` по-разному (`pageSize ?? 0` обнуляло смещение).
 */
const toWireParams = (request: ISearchRequest) => ({
    text: request.text,
    skip: request.pageIndex * request.pageSize,
    take: request.pageSize,
    orderByName: request.orderBy?.propName ?? '',
    orderByAsc: request.orderBy?.asc ?? false,
    requireTotalCount: request.requireTotalCount,
});

export class SearchService {
    static async searchDocuments(
        request: ISearchRequest,
        signal?: AbortSignal,
    ): Promise<AxiosResponse<ISearchDocResults>> {
        return $api.post<ISearchDocResults>('/search/searchDocs', SearchService.getSearchData(request), { signal });
    }

    static async searchDocumentsAI(
        request: ISearchRequest,
        signal?: AbortSignal,
    ): Promise<AxiosResponse<IActionResultData<string>>> {
        return $api.post<IActionResultData<string>>('/search/searchDocsAI', SearchService.getSearchData(request), {
            signal,
        });
    }

    static getSearchData(request: ISearchRequest) {
        return {
            ...toWireParams(request),
            extraFilter: {
                baseRequisites: request.filter?.baseRequisites ?? {},
                selectedFlowName: request.filter?.flowsFields?.selectedFlow?.key,
                fieldsValues: request.filter?.flowsFields?.fieldsValues,
            },
        };
    }

    static async searchDicts(request: ISearchRequest, signal?: AbortSignal): Promise<AxiosResponse<ISearchDictResults>> {
        return $api.get<ISearchDictResults>(`/search/searchDicts`, { params: toWireParams(request), signal });
    }

    static async searchReports(
        request: ISearchRequest,
        signal?: AbortSignal,
    ): Promise<AxiosResponse<ISearchReportResults>> {
        return $api.get<ISearchReportResults>(`/search/searchReports`, { params: toWireParams(request), signal });
    }

    static async loadExtraFilterFlowsData(): Promise<AxiosResponse<ISearchExtraFlowSetting[]>> {
        return $api.get<ISearchExtraFlowSetting[]>(`/search/getFlowSearchReqs`);
    }

    static async loadHistorySearchItems(): Promise<AxiosResponse<IHistorySearchItem[]>> {
        return $api.get<IHistorySearchItem[]>(`/search/historySearchItems`);
    }

    static async removeHistorySearchItem(id: number): Promise<AxiosResponse<IHistorySearchItem[]>> {
        return $api.delete<IHistorySearchItem[]>(`/search/historySearchItems/${id}`);
    }

    static async getSearchPanelScheme() {
        return $api.get<IForms>(`/search/getSearchScheme`);
    }

    static async getTabsVisibility() {
        return $api.get<ITabsVisibility>(`/search/getTabsVisibility`);
    }

    static async getResultTilesSetting() {
        return $api.get<IResultTilesSetting>(`/search/getResultTilesSetting`);
    }
}
